import { create } from 'zustand'
import { produce } from 'immer'
import { nanoid } from 'nanoid'
import type { Project, Space, SpaceKindId } from '@/model/types'
import { createDefaultProject, ordinalFloorName } from '@/model/defaults'
import { snap } from '@/lib/units'

const MAX_HISTORY = 100

/** Editor tools available in the 2D workspace. */
export type Tool =
  | 'select'
  | 'plot' // edit the plot boundary
  | 'space-rect'
  | 'space-poly'
  | 'opening' // place doors / windows / gate
  | 'prop'
  | 'pan'

/** Top-level workspace mode: the 2D editor vs the 3D visualizer. UI-only (not persisted). */
export type Mode = 'design' | 'visualize'

/** What is currently selected in the editor. Spaces support multi-select. */
export type Selection =
  | { kind: 'spaces'; ids: string[] }
  | { kind: 'opening'; ownerId: string; id: string } // ownerId = spaceId or 'compound'
  | { kind: 'prop'; id: string }
  | { kind: 'plot' }
  | null

export interface ViewTransform {
  scale: number // pixels per foot
  x: number // stage offset px
  y: number
}

interface EditorState {
  project: Project
  activeLevelId: string
  /** Design (2D editor) vs Visualize (3D). Not persisted in the saved project. */
  mode: Mode
  /**
   * Viewing someone else's shared design (from a `#d=` link). UI-only, never persisted.
   * While true, ALL project mutation is blocked (see `update`/`beginHistory`) so the
   * shared design — and the user's own local autosave — stay untouched until they Fork.
   */
  readOnly: boolean
  tool: Tool
  selection: Selection
  /** Space kind applied to newly drawn spaces. */
  drawKind: SpaceKindId
  /** Active prop preset id (from families/presets.ts). */
  activePresetId: string
  /** Active opening preset id (from families/presets.ts). */
  activeOpeningPresetId: string
  /** When set, the inspector focuses that space's name field once (e.g. just after drawing). */
  nameFocusId: string | null
  /** Copy/paste buffer for spaces. Store-only — never persisted into the project or history. */
  clipboard: Space[] | null
  view: ViewTransform

  past: Project[]
  future: Project[]

  /** Mutate the project via an immer recipe. Pushes to history unless transient. */
  update: (recipe: (draft: Project) => void, opts?: { transient?: boolean }) => void
  /** Snapshot current state into history once (use before a run of transient updates, e.g. a drag). */
  beginHistory: () => void
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean

  loadProject: (project: Project) => void
  newProject: () => void

  /** Enter/leave read-only share-viewing mode (UI-only). */
  setReadOnly: (readOnly: boolean) => void
  /**
   * Fork the currently-viewed shared design into a fresh, editable local project
   * (new id + "Copy of …" name), leave read-only mode, and reset history/selection.
   * The caller clears the `#d=` hash so a refresh doesn't re-enter view mode.
   */
  forkShared: () => void

  setMode: (mode: Mode) => void
  setTool: (tool: Tool) => void
  select: (selection: Selection) => void
  /** Select exactly these spaces (replaces current selection). */
  selectSpaces: (ids: string[]) => void
  /** Add/remove one space from the current selection (shift-click). */
  toggleSpaceSelection: (id: string) => void
  /** Select every space on the active level. */
  selectAllSpaces: () => void
  setDrawKind: (kind: SpaceKindId) => void
  setActivePresetId: (id: string) => void
  setActiveOpeningPresetId: (id: string) => void
  setNameFocus: (id: string | null) => void
  setActiveLevel: (id: string) => void
  setView: (view: ViewTransform) => void

  /** Copy the currently selected spaces into the clipboard (no-op unless spaces are selected). */
  copySelection: () => void
  /** Paste clipboard spaces (fresh ids, grid-snapped nudge) onto the active level; selects them. */
  paste: () => void
  /** Clone a whole floor (spaces + openings + props) onto a new level above; makes it active. */
  duplicateFloor: (levelId: string) => void

  /** Toggle whether `edgeIndex` of `spaceId` is open (no wall). One history entry per call. */
  toggleSpaceEdgeWall: (spaceId: string, edgeIndex: number) => void
}

/** Deep-clone a space with brand-new ids (space + every opening), optionally shifting its geometry. */
function cloneSpace(s: Space, offset: { x: number; y: number } | null): Space {
  const shift = (p: { x: number; y: number }) =>
    offset ? { x: p.x + offset.x, y: p.y + offset.y } : { x: p.x, y: p.y }
  return {
    ...structuredClone(s),
    id: nanoid(),
    polygon: s.polygon.map(shift),
    labelPos: s.labelPos ? shift(s.labelPos) : undefined,
    openings: s.openings.map((o) => ({ ...o, id: nanoid() })),
  }
}

const initial = createDefaultProject()

export const useEditor = create<EditorState>((set, get) => ({
  project: initial,
  activeLevelId: initial.levels[0].id,
  mode: 'design',
  readOnly: false,
  tool: 'select',
  selection: null,
  drawKind: 'room',
  activePresetId: 'bed-double',
  activeOpeningPresetId: 'door-internal',
  nameFocusId: null,
  clipboard: null,
  view: { scale: 12, x: 80, y: 80 }, // 12 px/ft default zoom

  past: [],
  future: [],

  update: (recipe, opts) =>
    set((state) => {
      if (state.readOnly) return {} // shared design is view-only — block all mutation
      const next = produce(state.project, (draft) => {
        recipe(draft)
        draft.updatedAt = Date.now()
      })
      if (next === state.project) return {} // no change
      if (opts?.transient) {
        return { project: next }
      }
      const past = [...state.past, state.project]
      if (past.length > MAX_HISTORY) past.shift()
      return { project: next, past, future: [] }
    }),

  beginHistory: () =>
    set((state) => {
      if (state.readOnly) return {}
      const past = [...state.past, state.project]
      if (past.length > MAX_HISTORY) past.shift()
      return { past, future: [] }
    }),

  undo: () =>
    set((state) => {
      const prev = state.past[state.past.length - 1]
      if (!prev) return {}
      return {
        project: prev,
        past: state.past.slice(0, -1),
        future: [state.project, ...state.future],
      }
    }),

  redo: () =>
    set((state) => {
      const next = state.future[0]
      if (!next) return {}
      return {
        project: next,
        past: [...state.past, state.project],
        future: state.future.slice(1),
      }
    }),

  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,

  loadProject: (project) =>
    set({
      project,
      activeLevelId: project.levels[0]?.id ?? '',
      selection: null,
      past: [],
      future: [],
    }),

  newProject: () => {
    const project = createDefaultProject()
    set({
      project,
      activeLevelId: project.levels[0].id,
      selection: null,
      past: [],
      future: [],
    })
  },

  setReadOnly: (readOnly) => set({ readOnly }),

  forkShared: () => {
    const src = get().project
    const copy: Project = {
      ...structuredClone(src),
      id: nanoid(),
      name: src.name.startsWith('Copy of ') ? src.name : `Copy of ${src.name}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
    set({
      project: copy,
      activeLevelId: copy.levels[0]?.id ?? '',
      readOnly: false,
      mode: 'design',
      tool: 'select',
      selection: null,
      past: [],
      future: [],
    })
  },

  setMode: (mode) => set({ mode }),
  setTool: (tool) => set({ tool, selection: tool === 'select' ? get().selection : null }),
  select: (selection) => set({ selection }),

  selectSpaces: (ids) => set({ selection: ids.length ? { kind: 'spaces', ids } : null }),

  toggleSpaceSelection: (id) =>
    set((state) => {
      const current = state.selection?.kind === 'spaces' ? state.selection.ids : []
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
      return { selection: next.length ? { kind: 'spaces', ids: next } : null }
    }),

  selectAllSpaces: () =>
    set((state) => {
      const lvl = activeLevel(state)
      const ids = lvl.spaces.map((s) => s.id)
      return { selection: ids.length ? { kind: 'spaces', ids } : null }
    }),

  setDrawKind: (drawKind) => set({ drawKind }),
  setActivePresetId: (activePresetId) => set({ activePresetId }),
  setActiveOpeningPresetId: (activeOpeningPresetId) => set({ activeOpeningPresetId }),
  setNameFocus: (nameFocusId) => set({ nameFocusId }),
  setActiveLevel: (id) => set({ activeLevelId: id, selection: null }),
  setView: (view) => set({ view }),

  copySelection: () => {
    const state = get()
    if (state.selection?.kind !== 'spaces') return
    const ids = new Set(state.selection.ids)
    const picked = state.project.levels.flatMap((l) => l.spaces.filter((s) => ids.has(s.id)))
    if (!picked.length) return
    set({ clipboard: structuredClone(picked) })
  },

  paste: () => {
    const state = get()
    const clip = state.clipboard
    if (!clip || !clip.length) return
    const nudge = snap(2, state.project.settings.gridSnap) || 2
    const pasted = clip.map((s) => cloneSpace(s, { x: nudge, y: nudge }))
    state.update((draft) => {
      const lvl = draft.levels.find((l) => l.id === state.activeLevelId) ?? draft.levels[0]
      lvl.spaces.push(...pasted)
    })
    set({ selection: { kind: 'spaces', ids: pasted.map((s) => s.id) } })
  },

  duplicateFloor: (levelId) => {
    const state = get()
    const src = state.project.levels.find((l) => l.id === levelId)
    if (!src) return
    const floorHeight = state.project.settings.standards.floorHeight
    const newId = nanoid()
    const spaces = src.spaces.map((s) => cloneSpace(s, null))
    const props = src.props.map((p) => ({ ...structuredClone(p), id: nanoid() }))
    const footprint = src.footprint.map((p) => ({ x: p.x, y: p.y }))
    state.update((draft) => {
      const source = draft.levels.find((l) => l.id === levelId)
      if (!source) return
      const top = draft.levels.reduce((m, l) => Math.max(m, l.baseElevation), -Infinity)
      let baseElevation = source.baseElevation + floorHeight
      if (draft.levels.some((l) => Math.abs(l.baseElevation - baseElevation) < 1e-6)) {
        baseElevation = top + floorHeight
      }
      draft.levels.push({
        id: newId,
        name: ordinalFloorName(draft.levels.length),
        baseElevation,
        height: source.height,
        footprint,
        spaces,
        props,
      })
    })
    set({ activeLevelId: newId, selection: null })
  },

  toggleSpaceEdgeWall: (spaceId, edgeIndex) => {
    get().update((draft) => {
      const sp = draft.levels.flatMap((l) => l.spaces).find((s) => s.id === spaceId)
      if (!sp) return
      const set = new Set(sp.openEdges ?? [])
      if (set.has(edgeIndex)) set.delete(edgeIndex)
      else set.add(edgeIndex)
      sp.openEdges = [...set].sort((a, b) => a - b)
    })
  },
}))

/** Selector helper: the currently active level (or the first one). */
export function activeLevel(state: EditorState) {
  return state.project.levels.find((l) => l.id === state.activeLevelId) ?? state.project.levels[0]
}
